/**
 * 납품 전 QA(품질 게이트) 아코디언 패널.
 *
 * 백엔드 QA 리포트를 등급(block/warn)별로 접이식으로 보여준다.
 *   - block: 정책상 존재하면 안 되는 케이스(다운로드 차단) — 빨강
 *   - warn : 확인 후 진행 케이스(정합성/형식) — 주황
 *
 * 다운로드 활성/비활성 및 확인창은 호출부(CsvDownloadButtons 등)에서 처리하고,
 * 이 컴포넌트는 표시만 담당한다.
 */

import { useState } from "react";
import {
  AlertOctagon,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Loader2,
  Pencil,
} from "lucide-react";
import { useTranslation } from "react-i18next";

import type { QaIssueGroup, QaIssueItem, QaReport } from "@/lib/api/qa";
import { cn } from "@/lib/cn";

interface Props {
  report: QaReport | undefined;
  isLoading: boolean;
  isError: boolean;
  /** 있으면 각 항목이 클릭 가능해지고, 클릭 시 해당 케이스 편집 모달을 연다. */
  onEditItem?: (item: QaIssueItem) => void;
}

export default function DeliveryQaPanel({ report, isLoading, isError, onEditItem }: Props) {
  const { t } = useTranslation();
  const [openCodes, setOpenCodes] = useState<Record<string, boolean>>({});

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        {t("qa.loading")}
      </div>
    );
  }

  if (isError || !report) return null;

  // 문제가 없을 때는 '통과/문제 없음'을 표시하지 않는다(FB 2026-09-09).
  // 이 게이트는 치명적 오류를 '경고'할 뿐, 검수를 거쳤다고 무결함을 보장하지 않는다.
  // 잘못된 안심을 주지 않도록 경고가 있을 때만 패널을 노출한다.
  if (report.groups.length === 0) return null;

  const blockCount = report.groups
    .filter((g) => g.severity === "block")
    .reduce((n, g) => n + g.count, 0);
  const warnCount = report.groups
    .filter((g) => g.severity === "warn")
    .reduce((n, g) => n + g.count, 0);

  const toggle = (code: string) => setOpenCodes((p) => ({ ...p, [code]: !p[code] }));

  return (
    <div
      className={cn(
        "rounded-md border",
        report.blocking ? "border-red-200 bg-red-50/40" : "border-amber-200 bg-amber-50/40",
      )}
    >
      {/* 요약 헤더 */}
      <div className="flex items-center gap-2 px-3 py-2 text-xs">
        {report.blocking ? (
          <AlertOctagon className="h-4 w-4 flex-shrink-0 text-red-600" />
        ) : (
          <AlertTriangle className="h-4 w-4 flex-shrink-0 text-amber-600" />
        )}
        <span className="font-semibold text-slate-800">{t("qa.title")}</span>
        {blockCount > 0 && (
          <span className="rounded bg-red-100 px-1.5 py-0.5 font-medium text-red-700">
            {t("qa.summary.block", { count: blockCount })}
          </span>
        )}
        {warnCount > 0 && (
          <span className="rounded bg-amber-100 px-1.5 py-0.5 font-medium text-amber-700">
            {t("qa.summary.warn", { count: warnCount })}
          </span>
        )}
      </div>

      {report.blocking && (
        <p className="px-3 pb-2 text-[11px] text-red-700">{t("qa.blockingHint")}</p>
      )}

      {/* 그룹별 아코디언 */}
      <div className="space-y-1 px-2 pb-2">
        {report.groups.map((g) => (
          <QaGroupRow
            key={g.code}
            group={g}
            open={!!openCodes[g.code]}
            onToggle={() => toggle(g.code)}
            onEditItem={onEditItem}
          />
        ))}
      </div>
    </div>
  );
}

function QaGroupRow({
  group,
  open,
  onToggle,
  onEditItem,
}: {
  group: QaIssueGroup;
  open: boolean;
  onToggle: () => void;
  onEditItem?: (item: QaIssueItem) => void;
}) {
  const { t } = useTranslation();
  const isBlock = group.severity === "block";

  return (
    <div className="overflow-hidden rounded border border-slate-200 bg-white">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-xs transition hover:bg-slate-50"
      >
        {open ? (
          <ChevronDown className="h-3.5 w-3.5 flex-shrink-0 text-slate-400" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 flex-shrink-0 text-slate-400" />
        )}
        {isBlock ? (
          <AlertOctagon className="h-3.5 w-3.5 flex-shrink-0 text-red-500" />
        ) : (
          <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 text-amber-500" />
        )}
        <span className="font-medium text-slate-800">{t(`qa.issue.${group.code}.label`)}</span>
        <span
          className={cn(
            "rounded px-1.5 py-0.5 text-[10px] font-medium",
            isBlock ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700",
          )}
        >
          {group.count}
        </span>
      </button>

      {open && (
        <div className="border-t border-slate-100 px-3 py-2">
          <p className="mb-1.5 text-[11px] text-slate-500">{t(`qa.issue.${group.code}.hint`)}</p>
          <ul className="max-h-56 space-y-1 overflow-y-auto">
            {group.items.map((it, idx) => {
              const body = (
                <>
                  <span className="font-medium text-slate-800">{it.document_id || it.file_id}</span>
                  {it.detail && <span className="text-slate-500"> — {it.detail}</span>}
                </>
              );
              return (
                <li key={`${it.file_id}-${idx}`}>
                  {onEditItem ? (
                    <button
                      type="button"
                      onClick={() => onEditItem(it)}
                      title={t("qa.editHint")}
                      className="flex w-full items-center gap-1.5 rounded bg-slate-50 px-2 py-1 text-left text-[11px] text-slate-700 transition hover:bg-blue-50 hover:text-blue-700"
                    >
                      <Pencil className="h-3 w-3 flex-shrink-0 text-slate-400" />
                      <span className="min-w-0 truncate">{body}</span>
                    </button>
                  ) : (
                    <span className="block rounded bg-slate-50 px-2 py-1 text-[11px] text-slate-700">
                      {body}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
